// LiveCallStrip.js
// App root pe render hoti hai (GlobalCallBanner ke bagal me), par dikhti sirf
// /fast-agent-detail pe. Tap karo to: ek call ho to seedha usi call me, warna list.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./LiveCallStrip.module.css";
import { getActiveLiveCalls } from "../../Store/apiStore";
import { getNotificationSocket } from "../../utils/initNotificationSocket";
import { useNotificationStore } from "../../Store/notificationStore";
import { useTeamStore } from "../../Store/teamStore";
import { useGlobalCall } from "../../contexts/GlobalCallContext";

const STRIP_ROUTES = ["/fast-agent-detail"];

const isRouteMatch = (pathname, routes) =>
  routes.some((r) => pathname === r || pathname.startsWith(`${r}/`));

const LiveCallStrip = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isCallLive } = useGlobalCall();
  const notifications = useNotificationStore((s) => s.notifications);
  const isTeamMemberStore = useTeamStore((s) => s.isTeamMember);

  const [calls, setCalls] = useState([]);
  const [checking, setChecking] = useState(false);
  const [socketTick, setSocketTick] = useState(0);

  // Team member (admin/support) ko live monitoring nahi dikhani — More menu me
  // bhi hataya hua hai. Backend waise bhi sirf apne agents lautata hai.
  const isTeamMember = isTeamMemberStore || !!localStorage.getItem("teamOwnerId");
  const onStripRoute = isRouteMatch(location.pathname, STRIP_ROUTES);
  // Apni hi web call chal rahi ho to strip nahi — GlobalCallBanner already khada hai.
  const active = onStripRoute && !isTeamMember && !isCallLive;

  // Nayi live call ka notification aaya kya — sirf refetch ka trigger hai,
  // sach hamesha API se lete hain (notification store IndexedDB me persist hota
  // hai, to usme purani entries bhi padi rehti hain).
  const latestLiveNotifId =
    (notifications || []).find(
      (n) => typeof n?.clickAction === "string" && n.clickAction.startsWith("/live-monitoring/")
    )?.id ?? null;

  // Khatam ho chuki calls. Retell ki list cut ho chuki call ko bhi kuch second
  // tak "ongoing" batati hai, isliye fetch ka nateeja bhi isse chhanta hai.
  const endedRef = useRef(new Set());

  const refresh = useCallback(async () => {
    try {
      const data = await getActiveLiveCalls();
      const list = Array.isArray(data?.calls) ? data.calls : [];
      return list.filter((c) => !endedRef.current.has(c.call_id));
    } catch (err) {
      console.error("[LiveCallStrip] active calls fetch failed:", err?.message || err);
      return [];
    }
  }, []);

  useEffect(() => {
    if (!active) {
      setCalls([]);
      // Navigate karke wapas aane par strip "Checking…" pe atki na rahe.
      setChecking(false);
      return undefined;
    }
    let alive = true;
    refresh().then((list) => {
      if (alive) setCalls(list);
    });
    return () => {
      alive = false;
    };
  }, [active, latestLiveNotifId, refresh]);

  // Call khatam hone ka signal — iske bina strip cut ho chuki call dikhati rehti
  // thi. `active` gate ke BAHAR hai jaan-boojh ke: apni hi call cut hone par
  // active usi pal true hota hai, to gated listener ye event miss kar jaata.
  useEffect(() => {
    const socket = getNotificationSocket();
    // App.js socket ko apne effect me banata hai, aur child effect parent se
    // PEHLE chalta hai — pehle mount pe socket null milta hai. Bina retry ke
    // /fast-agent-detail pe seedha refresh karne wale ka listener kabhi lagta
    // hi nahi. Socket milte hi ye retry apne aap ruk jaata hai.
    if (!socket) {
      const t = setTimeout(() => setSocketTick((n) => n + 1), 1000);
      return () => clearTimeout(t);
    }
    const onEnded = (payload) => {
      if (!payload?.callId) return;
      endedRef.current.add(payload.callId);
      setCalls((prev) => prev.filter((c) => c.call_id !== payload.callId));
    };
    socket.on("live_call_ended", onEnded);
    return () => socket.off("live_call_ended", onEnded);
  }, [location.pathname, latestLiveNotifId, socketTick]);

  // live_call_ended kabhi na aaye (Retell monitor WS fail) to strip atak sakti
  // hai — isliye click pe dobara pooch lete hain, phir navigate.
  const handleOpen = async () => {
    if (checking) return;
    setChecking(true);
    const fresh = await refresh();
    setCalls(fresh);
    // Navigate se PEHLE setChecking(false) mat karo — React wo render commit kar
    // deta hai aur "View" ek frame ke liye wapas flash karta hai. Route badalte
    // hi upar wala effect ise khud reset kar deta hai.
    if (fresh.length === 0) {
      setChecking(false);
      return;
    }
    if (fresh.length === 1 && fresh[0]?.call_id) {
      navigate(`/live-monitoring/${fresh[0].call_id}`);
      return;
    }
    navigate("/live-monitoring/active-calls");
  };

  if (!active || calls.length === 0) return null;

  const single = calls.length === 1;
  const label = single
    ? `Live call on ${calls[0]?.agentName || "your agent"}`
    : `${calls.length} live calls on your agents`;

  return (
    <button type="button" className={styles.strip} onClick={handleOpen} aria-live="polite">
      <span className={styles.dot} aria-hidden="true" />
      <span className={styles.label}>{label}</span>
      <span className={styles.cta}>{checking ? "Checking…" : "View"}</span>
    </button>
  );
};

export default LiveCallStrip;
